import { useQuery } from "@tanstack/react-query";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle } from "lucide-react";
import { fetchSqlSyncTrends, SqlSyncTrendPoint } from "@/lib/api";

const ENTITY_COLORS: Record<string, string> = {
  Series: "#2563eb",
  Title: "#16a34a",
  Product: "#d97706",
  MainSeries: "#9333ea",
  Contract: "#dc2626",
};

function getColor(entityType: string): string {
  return ENTITY_COLORS[entityType] || "#6b7280";
}

interface ChartDataPoint {
  timestamp: number;
  time: string;
  entityType: string;
  durationMinutes: number;
  jobId: string;
  status: string;
  totalRecords: number;
}

function buildChartData(trends: SqlSyncTrendPoint[]): {
  series: Record<string, ChartDataPoint[]>;
  entityTypes: string[];
} {
  const sorted = [...trends]
    .filter((t) => Number.isFinite(t.durationMinutes) && t.durationMinutes >= 0)
    .sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime());

  const entityTypes = Array.from(new Set(sorted.map((t) => t.entityType)));
  const series: Record<string, ChartDataPoint[]> = {};

  for (const trend of sorted) {
    const timestamp = new Date(trend.startedAt).getTime();
    if (!series[trend.entityType]) {
      series[trend.entityType] = [];
    }
    series[trend.entityType].push({
      timestamp,
      time: new Date(trend.startedAt).toLocaleString("en-GB", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
      entityType: trend.entityType,
      durationMinutes: trend.durationMinutes,
      jobId: trend.jobId,
      status: trend.status,
      totalRecords: trend.totalRecords,
    });
  }

  return { series, entityTypes };
}

export function SyncDurationChart({ days = 7 }: { days?: number }) {
  const { data: trendsData, isLoading, error } = useQuery({
    queryKey: ["sqlSyncTrends", days],
    queryFn: () => fetchSqlSyncTrends(days),
    staleTime: 60000,
  });

  if (isLoading) {
    return <Skeleton className="h-72 w-full" />;
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
        <AlertCircle className="h-4 w-4" />
        Failed to load trends
      </div>
    );
  }

  const trends = trendsData?.trends || [];
  const { series, entityTypes } = buildChartData(trends);
  const totalPoints = entityTypes.reduce((sum, entityType) => sum + (series[entityType]?.length || 0), 0);

  if (totalPoints === 0) {
    return (
      <div className="text-sm text-muted-foreground text-center py-8">
        No completed sync jobs in the last {days} days
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-4 bg-background">
      <h3 className="text-sm font-medium mb-4">Sync Duration Trends</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="timestamp"
              type="number"
              domain={["dataMin", "dataMax"]}
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              tickFormatter={(value) =>
                new Date(value).toLocaleString("en-GB", {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })
              }
            />
            <YAxis
              dataKey="durationMinutes"
              type="number"
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              label={{
                value: "Minutes",
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 11, fill: "hsl(var(--muted-foreground))" },
              }}
            />
            <Tooltip
              cursor={{ strokeDasharray: "3 3" }}
              contentStyle={{
                backgroundColor: "hsl(var(--background))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "6px",
                fontSize: "12px",
              }}
              formatter={(value: number, _name, item) => {
                const point = item.payload as ChartDataPoint;
                return [`${value}m`, point.entityType];
              }}
              labelFormatter={(_value, payload) => {
                const point = payload?.[0]?.payload as ChartDataPoint | undefined;
                return point?.time || "";
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: "12px" }}
            />
            {entityTypes.map((entityType) => (
              <Scatter
                key={entityType}
                name={entityType}
                data={series[entityType]}
                fill={getColor(entityType)}
                line={{ stroke: getColor(entityType), strokeWidth: 2 }}
                lineType="joint"
              />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
